import {
  File,
  FileText,
  Image as ImageIcon,
  Video,
  Volume2,
} from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DownloadAction } from "@/components/common/media-preview/DownloadAction";
import {
  FILE_NAME_MAX_DISPLAY_WIDTH,
  truncateFileNameByDisplayWidth,
} from "@/components/common/media-preview/fileNameDisplay";
import type { MediaFile } from "@/components/common/media-preview/types";
import {
  CARD_INTERACTIVE_CLASS,
  formatFileSize,
  getFileExtension,
  getMediaDownloadUrl,
  getMediaKind,
  hasFreshSignedMediaUrl,
  TILE_FIXED_WIDTH_CLASS,
  type TileLayoutMode,
} from "@/components/common/media-preview/utils";
import { cn } from "@/lib/utils";

interface SingleMediaPreviewProps {
  file: MediaFile;
  index: number;
  onOpenPreview: (index: number) => void;
  mode?: "full" | "tile";
  layoutMode?: TileLayoutMode;
}

const TILE_LAYOUT_CLASS: Record<TileLayoutMode, string> = {
  "fill-column": "w-full justify-self-stretch",
  "fixed-width": `${TILE_FIXED_WIDTH_CLASS} justify-self-start`,
  "fit-content": "w-fit justify-self-start",
};

const ClickablePreview: React.FC<{
  index: number;
  onOpenPreview: (index: number) => void;
  className?: string;
  children: React.ReactNode;
}> = ({ index, onOpenPreview, className, children }) => (
  <button
    type="button"
    onClick={() => onOpenPreview(index)}
    className={className}
  >
    {children}
  </button>
);

const PREVIEW_CARD_BASE_CLASS =
  "min-w-0 max-w-full flex items-center gap-2 text-left";

const PreviewCard: React.FC<{
  index: number;
  onOpenPreview: (index: number) => void;
  className?: string;
  children: React.ReactNode;
  action: React.ReactNode;
}> = ({ index, onOpenPreview, className, children, action }) => (
  <div className={cn(CARD_INTERACTIVE_CLASS, "flex items-center", className)}>
    <ClickablePreview
      index={index}
      onOpenPreview={onOpenPreview}
      className={cn(PREVIEW_CARD_BASE_CLASS, "flex-1")}
    >
      {children}
    </ClickablePreview>
    {action}
  </div>
);

const FileMeta: React.FC<{
  fileName: string;
  subtitle: string;
}> = ({ fileName, subtitle }) => (
  <div className="min-w-0 flex-1">
    <div className="truncate text-sm font-medium text-gray-900">{fileName}</div>
    <div className="text-xs text-gray-500">{subtitle}</div>
  </div>
);

export const SingleMediaPreview: React.FC<SingleMediaPreviewProps> = ({
  file,
  index,
  onOpenPreview,
  mode = "full",
  layoutMode = "fit-content",
}) => {
  const { t } = useTranslation();
  const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);

  const mediaKind = getMediaKind(file);
  const displayFileName = truncateFileNameByDisplayWidth(
    file.file_name,
    FILE_NAME_MAX_DISPLAY_WIDTH,
  );
  const isImage = mediaKind === "image";
  const isVideo = mediaKind === "video";
  const isAudio = mediaKind === "audio";
  const isPdf = mediaKind === "pdf";
  const thumbnailUrl = file.thumbnail_url;
  const downloadUrl = getMediaDownloadUrl(file);
  const shouldUseDirectDownload = hasFreshSignedMediaUrl(file);

  if (mode === "tile") {
    const extension = getFileExtension(file.file_name);
    const fileTypeLabel = isImage
      ? t("common.image")
      : isVideo
        ? t("common.fileTypeVideo")
        : isAudio
          ? t("common.fileTypeAudio")
          : isPdf
            ? t("common.fileTypePdf")
            : extension.toUpperCase() || t("common.fileTypeFile");

    return (
      <PreviewCard
        index={index}
        onOpenPreview={onOpenPreview}
        className={cn("min-h-[72px]", TILE_LAYOUT_CLASS[layoutMode])}
        action={
          <DownloadAction
            guid={file.guid}
            fileName={file.file_name}
            downloadUrl={downloadUrl}
            useDirectDownload={shouldUseDirectDownload}
            accessUrlExpiresAt={file.access_url_expires_at}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
          />
        }
      >
        {isImage && thumbnailUrl && failedImageUrl !== thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={file.file_name}
            className="size-11 shrink-0 rounded object-cover"
            onError={() => setFailedImageUrl(thumbnailUrl)}
            loading="lazy"
          />
        ) : isImage ? (
          <ImageIcon className="size-7 shrink-0 text-indigo-500" />
        ) : isVideo ? (
          <Video className="size-7 shrink-0 text-violet-500" />
        ) : isAudio ? (
          <Volume2 className="size-7 shrink-0 text-emerald-500" />
        ) : isPdf ? (
          <FileText className="size-7 shrink-0 text-red-500" />
        ) : (
          <File className="size-7 shrink-0 text-gray-400" />
        )}

        <FileMeta
          fileName={displayFileName}
          subtitle={`${fileTypeLabel} • ${formatFileSize(file.size)}`}
        />
      </PreviewCard>
    );
  }

  if (isImage && thumbnailUrl && failedImageUrl !== thumbnailUrl) {
    return (
      <div className="w-fit max-w-full">
        <ClickablePreview
          index={index}
          onOpenPreview={onOpenPreview}
          className="group relative max-w-full cursor-zoom-in transition-opacity hover:opacity-95"
        >
          <img
            src={thumbnailUrl}
            alt={file.file_name}
            className="max-h-[360px] max-w-full rounded-md bg-gray-50 object-contain shadow-sm"
            onError={() => setFailedImageUrl(thumbnailUrl)}
            loading="lazy"
          />
        </ClickablePreview>
        <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
          <span className="truncate">{displayFileName}</span>
          <DownloadAction
            guid={file.guid}
            fileName={file.file_name}
            downloadUrl={downloadUrl}
            useDirectDownload={shouldUseDirectDownload}
            accessUrlExpiresAt={file.access_url_expires_at}
            className="shrink-0 p-1 text-gray-400 hover:text-gray-600"
          />
        </div>
      </div>
    );
  }

  const previewIcon = isImage ? (
    <ImageIcon className="size-5 shrink-0 text-indigo-500" />
  ) : isVideo ? (
    <Video className="size-5 shrink-0 text-violet-500" />
  ) : isAudio ? (
    <Volume2 className="size-5 shrink-0 text-emerald-500" />
  ) : isPdf ? (
    <FileText className="size-5 text-red-500" />
  ) : (
    <File className="size-5 text-gray-400" />
  );

  const subtitle = isPdf
    ? `${getFileExtension(file.file_name).toUpperCase()} • ${formatFileSize(file.size)}`
    : formatFileSize(file.size);

  return (
    <PreviewCard
      index={index}
      onOpenPreview={onOpenPreview}
      action={
        <DownloadAction
          guid={file.guid}
          fileName={file.file_name}
          downloadUrl={downloadUrl}
          useDirectDownload={shouldUseDirectDownload}
          accessUrlExpiresAt={file.access_url_expires_at}
          className="p-1 text-gray-400 hover:text-gray-600"
        />
      }
    >
      {previewIcon}
      <FileMeta fileName={displayFileName} subtitle={subtitle} />
    </PreviewCard>
  );
};
